import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as AIIcon } from '../../icons/AIIcon.svg';
import GenAIResultView from './GenAIResultView';
import { useUpdateBusinessDetails } from '../../hooks/businesshooks';
import { useCompleteSignIn } from '../../hooks/authhooks';
import { BusinessReadiness, MyBusinessSchema } from '../../data/types';

type GetAIViewProps = {
    isOpen: boolean;
    business: MyBusinessSchema | null;
    wasUpdate?: boolean;
    durationInSeconds?: number;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDreamModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/** Load the persisted business-readiness profile while replaying the stepped progress animation. */
const GenAIView: React.FC<GetAIViewProps> = (props) => {
    const { isOpen, business } = props;
    const { user } = useCompleteSignIn();
    const uid = user?.uid || '';
    const [readiness, setReadiness] = useState<BusinessReadiness | null>(null);
    const [error, setError] = useState('');
    const [animationComplete, setAnimationComplete] = useState(false);
    const { createBusinessReadiness } = useUpdateBusinessDetails(uid);
    const fetchSettled = Boolean(readiness || error);

    useEffect(() => {
        if (!isOpen || !uid || !business?.id) {
            setReadiness(null);
            setError('');
            setAnimationComplete(false);
            return;
        }
        let active = true;
        setReadiness(null);
        setError('');
        setAnimationComplete(false);
        void createBusinessReadiness(business.id)
            .then((result) => {
                if (!active) return;
                if (result) setReadiness(result);
                else setError('Your business was saved, but readiness details could not be loaded.');
            })
            .catch(() => {
                if (active) setError('Your business was saved, but readiness details could not be loaded.');
            });
        return () => {
            active = false;
        };
        // createBusinessReadiness is a mutation wrapper and intentionally keyed by the saved id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business?.id, isOpen, uid]);

    if (animationComplete && fetchSettled) {
        return <GenAIResultView {...props} readiness={readiness} error={error} />;
    }

    return <GenAILoadingView {...props} setCompleted={setAnimationComplete} />;
}

type GenAILoadingViewProps = {
    isOpen: boolean;
    durationInSeconds?: number;
    setCompleted: React.Dispatch<React.SetStateAction<boolean>>;
}

/** Stepped progress animation shown while business readiness is generated. */
const GenAILoadingView: React.FC<GenAILoadingViewProps> = (props) => {
    const { isOpen, durationInSeconds = 40, setCompleted } = props;
    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const [shouldComplete, setShouldComplete] = useState(false);

    const steps = useMemo(() => [
        { title: 'Reviewing your submissions', targetProgress: 10, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-400" /> },
        { title: 'Creating your profile', targetProgress: 40, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-200 scale-y-[-1]" /> },
        { title: 'Determining your business stage', targetProgress: 80, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-300" /> },
        { title: 'Generating your checklist', targetProgress: 100, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-200 scale-y-[-1]" /> },
    ], []);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(0);
            setProgress(0);
            setShouldComplete(false);
            return;
        }

        const stepDuration = (durationInSeconds * 1000) / steps.length;
        const progressInterval = 50;

        const timer = setInterval(() => {
            setProgress((prev) => {
                const currentTarget = steps[currentStep]?.targetProgress || 100;
                const previousTarget = steps[currentStep - 1]?.targetProgress || 0;
                const nextProgress = Math.min(
                    prev + (progressInterval / stepDuration) * (currentTarget - previousTarget),
                    currentTarget
                );

                if (nextProgress >= currentTarget) {
                    if (currentStep < steps.length - 1) {
                        setCurrentStep((step) => step + 1);
                    } else if (currentStep === steps.length - 1) {
                        setShouldComplete(true);
                    }
                }

                return nextProgress;
            });
        }, progressInterval);

        return () => clearInterval(timer);
    }, [isOpen, steps, currentStep, durationInSeconds]);

    useEffect(() => {
        if (shouldComplete) {
            setCompleted(true);
        }
    }, [shouldComplete, setCompleted]);

    return (
        <div className="flex min-h-[300px] flex-col items-center justify-center p-8" role="status" data-testid="gen-ai-loading">
            <div className="w-full max-w-md">
                {steps[currentStep]?.Icon}
                <div className="my-6 flex items-center justify-center">
                    <span className="text-center font-raleway text-[24px] font-bold text-primaryDark-100" data-testid="gen-ai-loading-step">
                        {steps[currentStep]?.title}
                    </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                        className="h-2.5 rounded-full bg-[#5DB6CE] transition-all duration-300 ease-out"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="mt-2 text-right font-raleway text-[16px] font-semibold text-black">
                    {Math.round(progress)}%
                </div>
            </div>
        </div>
    );
};

export default GenAIView;
